/** Utility functions for providing purchase plan information **/
import gql from 'graphql-tag'
import type {
  Plan,
  SubscriptionOfferingFilter,
  SubscriptionProductsResponse,
} from '@/types/subscriptions'
import { Vertical } from '@/types/verticals'
// Lazy import to break circular dep: apollo-client → net → identity-client → … → subscription-service → marketing → plans → apollo-client
async function getApolloClientLazy() {
  const { getApolloClient } = await import('@/lib/apollo-client')
  return getApolloClient()
}

type Products = {
  [key in Vertical]: {
    result: {
      subscriptionOfferings: {
        tiers: Plan[]
      }
    }
  }
}

export enum HUMAN_SKUS {
  // Hunt
  HUNT_ELITE_YEAR = 'nationwide_year',
  HUNT_ELITE_MONTH = 'nationwide_month',
  HUNT_MULTI_STATE_YEAR = 'hunt_multi_state_year',
  HUNT_PREMIUM_YEAR = 'single_state_year',
  HUNT_ELITE_YEAR_AUTOSUBSCRIBE = 'nationwide_year_autosubscribe',
  HUNT_NATIONWIDE_MONTH_AUTOSUBSCRIBE = 'nationwide_month_autosubscribe',
  HUNT_MULTI_STATE_YEAR_AUTOSUBSCRIBE = 'hunt_multi_state_year_autosubscribe',
  HUNT_PREMIUM_YEAR_AUTOSUBSCRIBE = 'single_state_year_autosubscribe',
  // Offroad
  OFFROAD_ELITE_YEAR = 'offroad_elite_year',
  OFFROAD_ELITE_MONTH = 'offroad_elite_month',
  OFFROAD_PREMIUM_YEAR = 'offroad_premium_year',
  OFFROAD_ELITE_YEAR_AUTOSUBSCRIBE = 'offroad_elite_year_autosubscribe',
  OFFROAD_ELITE_MONTH_AUTOSUBSCRIBE = 'offroad_elite_month_autosubscribe',
  OFFROAD_PREMIUM_YEAR_AUTOSUBSCRIBE = 'offroad_premium_year_autosubscribe',
  // Backcountry
  BACKCOUNTRY_ELITE_YEAR = 'backcountry_elite_year',
  BACKCOUNTRY_PREMIUM_YEAR = 'backcountry_premium_year',
  BACKCOUNTRY_ELITE_YEAR_AUTOSUBSCRIBE = 'backcountry_elite_year_autosubscribe',
  BACKCOUNTRY_PREMIUM_YEAR_AUTOSUBSCRIBE = 'backcountry_premium_year_autosubscribe',
  // Fish
  FISH_ELITE_YEAR = 'fish_elite_year',
}

/**
 * Maps opaque sku to a legacy human-readable sku
 */
export const productSkuNames = {
  hunt: [
    // Standard products
    {
      sku: 'h_m6s02q3',
      humanSku: HUMAN_SKUS.HUNT_ELITE_YEAR,
    },
    {
      sku: 'h_d9hrpm6',
      humanSku: HUMAN_SKUS.HUNT_ELITE_MONTH,
    },
    {
      sku: 'h_p3rjg25',
      humanSku: HUMAN_SKUS.HUNT_MULTI_STATE_YEAR,
    },
    {
      sku: 'h__rw6578',
      humanSku: HUMAN_SKUS.HUNT_PREMIUM_YEAR,
    },
    // Autosubscribe products
    {
      sku: 'h_iodpt1i',
      humanSku: HUMAN_SKUS.HUNT_ELITE_YEAR_AUTOSUBSCRIBE,
    },
    {
      sku: 'h_42e1wak',
      humanSku: HUMAN_SKUS.HUNT_NATIONWIDE_MONTH_AUTOSUBSCRIBE,
    },
    {
      sku: 'h_zdc_xhd',
      humanSku: HUMAN_SKUS.HUNT_MULTI_STATE_YEAR_AUTOSUBSCRIBE,
    },
    {
      sku: 'h_xp_5mxb',
      humanSku: HUMAN_SKUS.HUNT_PREMIUM_YEAR_AUTOSUBSCRIBE,
    },
  ],
  offroad: [
    // Standard products
    {
      sku: 'offroad_elite_year',
      humanSku: HUMAN_SKUS.OFFROAD_ELITE_YEAR,
    },
    {
      sku: 'offroad_elite_month',
      humanSku: HUMAN_SKUS.OFFROAD_ELITE_MONTH,
    },
    {
      sku:
        window.environment.name !== 'production'
          ? 'price_1NFgfJIDmnNT2Uj6YUxocBI0'
          : 'price_1NFgOoIDmnNT2Uj6kHB4WvDY',
      humanSku: HUMAN_SKUS.OFFROAD_PREMIUM_YEAR,
    },
    // Autosubscribe products
    {
      sku: 'offroad_elite_year_autosubscribe',
      humanSku: HUMAN_SKUS.OFFROAD_ELITE_YEAR_AUTOSUBSCRIBE,
    },
    {
      sku: 'offroad_elite_month_autosubscribe',
      humanSku: HUMAN_SKUS.OFFROAD_ELITE_MONTH_AUTOSUBSCRIBE,
    },
    {
      sku:
        window.environment.name !== 'production'
          ? 'price_1NFgfcIDmnNT2Uj6fGFEreva'
          : 'price_1NFgQHIDmnNT2Uj68janpT5N',
      humanSku: HUMAN_SKUS.OFFROAD_PREMIUM_YEAR_AUTOSUBSCRIBE,
    },
  ],
  backcountry: [
    // Standard products
    {
      sku: 'backcountry_elite_year',
      humanSku: HUMAN_SKUS.BACKCOUNTRY_ELITE_YEAR,
    },
    {
      sku: 'backcountry_elite_month',
      humanSku: 'backcountry_elite_month',
    },
    {
      sku: 'backcountry_premium_year',
      humanSku: HUMAN_SKUS.BACKCOUNTRY_PREMIUM_YEAR,
    },
    // Autosubscribe products
    {
      sku: 'backcountry_elite_year_autosubscribe',
      humanSku: HUMAN_SKUS.BACKCOUNTRY_ELITE_YEAR_AUTOSUBSCRIBE,
    },
    {
      sku: 'backcountry_elite_month_autosubscribe',
      humanSku: 'backcountry_elite_month_autosubscribe',
    },
    {
      sku: 'backcountry_premium_year_autosubscribe',
      humanSku: HUMAN_SKUS.BACKCOUNTRY_PREMIUM_YEAR_AUTOSUBSCRIBE,
    },
  ],
  fish: [
    {
      sku:
        window.environment.name !== 'production'
          ? 'fish_elite_year_test'
          : 'fish_elite_year',
      humanSku: HUMAN_SKUS.FISH_ELITE_YEAR,
    },
  ],
} as const

export const defaultProducts: Products = {
  hunt: {
    result: {
      subscriptionOfferings: {
        tiers: [
          {
            sku: getDefaultOpaqueSku('nationwide_year', Vertical.Hunt),
            level: 'ELITE',
            name: 'Elite',
            description: [
              'All of USA + Canada',
              'Unlimited Offline Maps',
              'Elite-Exclusive Pro Deals and Expert Resources',
            ],
            price: 9999,
            term: {
              unit: 'YEAR',
              amount: 1,
            },
          },
          {
            sku: getDefaultOpaqueSku('nationwide_month', Vertical.Hunt),
            level: 'ELITE',
            name: 'Elite',
            description: [
              'All of USA + Canada',
              'Unlimited Offline Maps',
              'Elite-Exclusive Pro Deals and Expert Resources',
            ],
            price: 1499,
            term: {
              unit: 'MONTH',
              amount: 1,
            },
          },
          {
            sku: getDefaultOpaqueSku('hunt_multi_state_year', Vertical.Hunt),
            level: 'PREMIUM_MULTI_STATE',
            name: 'Premium +',
            description: [
              '2 States or 1 State + Canada',
              'Unlimited Offline Maps',
            ],
            price: 4999,
            term: {
              unit: 'YEAR',
              amount: 1,
            },
          },
          {
            sku: getDefaultOpaqueSku('single_state_year', Vertical.Hunt),
            level: 'PREMIUM',
            name: 'Premium',
            description: ['1 State or Canada', 'Unlimited Offline Maps'],
            price: 3499,
            term: {
              unit: 'YEAR',
              amount: 1,
            },
          },
        ],
      },
    },
  },
  offroad: {
    result: {
      subscriptionOfferings: {
        tiers: [
          {
            sku: getDefaultOpaqueSku('offroad_elite_year', Vertical.Offroad),
            level: 'ELITE',
            name: 'Offroad Elite',
            description: [
              'Elite-Exclusive Pro Deals',
              'Private Land Ownership in the US & Canada',
              'Offroad Trails & Public Lands in the US, Mexico & Canada',
              'Unlimited Offline Maps',
            ],
            price: 9999,
            term: {
              unit: 'YEAR',
              amount: 1,
            },
          },
          {
            sku: getDefaultOpaqueSku('offroad_elite_month', Vertical.Offroad),
            name: 'Elite Monthly',
            price: 1499,
            level: 'ELITE',
            description: [
              'Elite-Exclusive Pro Deals',
              'Private Land Ownership in the US & Canada',
              'Offroad Trails & Public Lands in the US, Mexico & Canada',
              'Unlimited Offline Maps',
            ],
            term: {
              amount: 1,
              unit: 'MONTH',
            },
          },
          {
            sku: getDefaultOpaqueSku('offroad_premium_year', Vertical.Offroad),
            level: 'PREMIUM',
            name: 'Offroad Premium',
            description: [
              'Offroad Trails & Public Lands in the US',
              'Unlimited Offline Maps',
            ],
            price: 3499,
            term: {
              unit: 'YEAR',
              amount: 1,
            },
          },
        ],
      },
    },
  },
  backcountry: {
    result: {
      subscriptionOfferings: {
        tiers: [
          {
            sku: getDefaultOpaqueSku(
              'backcountry_elite_year',
              Vertical.Backcountry
            ),
            level: 'ELITE',
            name: 'Elite Yearly',
            description: [
              'ALL Premium Features Included',
              'Unlimited 3D Offline Maps for No-Service Navigation',
              'Recent Imagery: Detailed satellite imagery from the last 2 weeks',
              '985 million acres of Public and Private Land across the U.S.',
            ],
            price: 9999,
            term: {
              unit: 'YEAR',
              amount: 1,
            },
          },
          {
            sku: getDefaultOpaqueSku(
              'backcountry_elite_month',
              Vertical.Backcountry
            ),
            level: 'ELITE',
            name: 'Elite Monthly',
            description: [
              'ALL Premium Features Included',
              'Unlimited 3D Offline Maps for No-Service Navigation',
              'Recent Imagery: Detailed satellite imagery from the last 2 weeks',
              '985 million acres of Public and Private Land across the U.S.',
            ],
            price: 1499,
            term: {
              unit: 'MONTH',
              amount: 1,
            },
          },
          {
            sku: getDefaultOpaqueSku(
              'backcountry_premium_year',
              Vertical.Backcountry
            ),
            level: 'PREMIUM',
            name: 'Premium',
            description: [
              '650,000+ miles of running, hiking, backpacking, skiing, and mountain bike trails',
              'LiDAR topo clarity, detailed guidebook descriptions, trip reports & trail info',
              '985 million acres of Public Land across the U.S.',
              'ATES and Avalanche Simulator, Slope Angle & Aspect Layers',
            ],
            price: 2999,
            term: {
              unit: 'YEAR',
              amount: 1,
            },
          },
        ],
      },
    },
  },
  fish: {
    result: {
      subscriptionOfferings: {
        tiers: [
          {
            sku: getDefaultOpaqueSku('fish_elite_year', Vertical.Fish),
            name: 'Midwest Region',
            level: 'ELITE',
            description: [
              'Currently available in select Midwestern states',
              'Filter by Lake and Species',
              'Trophy and Keeper Potential',
              'Public/Private Land Ownership',
            ],
            price: 3499,
            term: {
              amount: 1,
              unit: 'YEAR',
            },
            // Fish is autosubscribe only so default products should have trial periods even in pyp context
            trialTerm: {
              amount: 7,
              unit: 'DAY',
            },
          },
        ],
      },
    },
  },
}

export const onboardingDefaultProducts: Products = {
  hunt: {
    result: {
      subscriptionOfferings: {
        tiers: [
          {
            sku: getDefaultOpaqueSku(
              'nationwide_year_autosubscribe',
              Vertical.Hunt
            ),
            level: 'ELITE',
            name: 'Elite',
            description: ['All of USA + Canada'],
            price: 9999,
            trialTerm: {
              unit: 'DAY',
              amount: 7,
            },
            term: {
              unit: 'YEAR',
              amount: 1,
            },
          },
          {
            sku: getDefaultOpaqueSku(
              'nationwide_month_autosubscribe',
              Vertical.Hunt
            ),
            level: 'ELITE',
            name: 'Elite',
            description: ['All of USA + Canada'],
            price: 1499,
            trialTerm: {
              unit: 'DAY',
              amount: 7,
            },
            term: {
              unit: 'MONTH',
              amount: 1,
            },
          },
          {
            sku: getDefaultOpaqueSku(
              'hunt_multi_state_year_autosubscribe',
              Vertical.Hunt
            ),
            level: 'PREMIUM_MULTI_STATE',
            name: 'Premium +',
            description: ['2 States or 1 State + Canada'],
            price: 4999,
            trialTerm: {
              unit: 'DAY',
              amount: 7,
            },
            term: {
              unit: 'YEAR',
              amount: 1,
            },
          },
          {
            sku: getDefaultOpaqueSku(
              'single_state_year_autosubscribe',
              Vertical.Hunt
            ),
            level: 'PREMIUM',
            name: 'Premium',
            description: ['1 State or Canada'],
            price: 3499,
            trialTerm: {
              unit: 'DAY',
              amount: 7,
            },
            term: {
              unit: 'YEAR',
              amount: 1,
            },
          },
        ],
      },
    },
  },
  offroad: {
    result: {
      subscriptionOfferings: {
        tiers: [
          {
            sku: getDefaultOpaqueSku(
              'offroad_elite_year_autosubscribe',
              Vertical.Offroad
            ),
            level: 'ELITE',
            name: 'Elite',
            description: ['Private Lands and Motorized Trails'],
            price: 9999,
            trialTerm: {
              unit: 'DAY',
              amount: 7,
            },
            term: {
              unit: 'YEAR',
              amount: 1,
            },
          },
          {
            sku: getDefaultOpaqueSku(
              'offroad_elite_month_autosubscribe',
              Vertical.Offroad
            ),
            name: 'Elite Monthly',
            price: 1499,
            level: 'ELITE',
            description: ['Private Lands and Motorized Trails'],
            trialTerm: {
              unit: 'DAY',
              amount: 7,
            },
            term: {
              amount: 1,
              unit: 'MONTH',
            },
          },
          {
            sku: getDefaultOpaqueSku(
              'offroad_premium_year_autosubscribe',
              Vertical.Offroad
            ),
            level: 'PREMIUM',
            name: 'Premium',
            description: ['Motorized Trails'],
            price: 3499,
            trialTerm: {
              unit: 'DAY',
              amount: 7,
            },
            term: {
              unit: 'YEAR',
              amount: 1,
            },
          },
        ],
      },
    },
  },
  backcountry: {
    result: {
      subscriptionOfferings: {
        tiers: [
          {
            sku: getDefaultOpaqueSku(
              'backcountry_elite_year_autosubscribe',
              Vertical.Backcountry
            ),
            level: 'ELITE',
            name: 'Elite Yearly',
            description: [
              'Premium + Unlimited 3D offline & Private Land Ownership',
            ],
            price: 9999,
            trialTerm: {
              unit: 'DAY',
              amount: 7,
            },
            term: {
              unit: 'YEAR',
              amount: 1,
            },
          },
          {
            sku: getDefaultOpaqueSku(
              'backcountry_elite_month_autosubscribe',
              Vertical.Backcountry
            ),
            level: 'ELITE',
            name: 'Elite Monthly',
            description: [
              'Premium + Unlimited 3D offline & Private Land Ownership',
            ],
            price: 1499,
            trialTerm: {
              unit: 'DAY',
              amount: 7,
            },
            term: {
              unit: 'MONTH',
              amount: 1,
            },
          },
          {
            sku: getDefaultOpaqueSku(
              'backcountry_premium_year_autosubscribe',
              Vertical.Backcountry
            ),
            level: 'PREMIUM',
            name: 'Premium',
            description: [
              '650k+ miles of running, hiking, biking & ski trails',
            ],
            price: 2999,
            trialTerm: {
              unit: 'DAY',
              amount: 7,
            },
            term: {
              unit: 'YEAR',
              amount: 1,
            },
          },
        ],
      },
    },
  },
  fish: {
    result: {
      subscriptionOfferings: {
        tiers: [
          {
            sku: getDefaultOpaqueSku('fish_elite_year', Vertical.Fish),
            name: 'Midwest Region',
            level: 'ELITE',
            description: [
              'Currently available in select Midwestern states',
              'Filter by Lake and Species',
              'Trophy and Keeper Potential',
              'Public/Private Land Ownership',
            ],
            price: 3499,
            term: {
              amount: 1,
              unit: 'YEAR',
            },
            trialTerm: {
              amount: 7,
              unit: 'DAY',
            },
          },
        ],
      },
    },
  },
}

export type Sku = (typeof productSkuNames)[Vertical][number]['sku']
export type HumanSku = `${HUMAN_SKUS}`
/**
 * Gets a human readable sku for the provided opaque sku if one exists
 * @param sku - An opaque sku who's human readable sku we wish to retrieve
 */
export function getHumanSku(sku: string) {
  return productSkuNames['fish'].find((product) => {
    return product.sku === sku
  })?.humanSku
}

/**
 * Gets an opaque sku for the provided human readable sku if one exists
 * @param sku - A human readable sku who's opaque sku we wish to retrieve
 */
export function getOpaqueSku(sku: string) {
  return productSkuNames['fish'].find((product) => {
    return product.humanSku === sku
  })?.sku
}

/**
 * Gets an opaque sku for the provided human readable sku if one exists
 * @param sku a human readable sku
 * @param app a vertical to lookup the sku in
 * @returns the opaque sku if found, otherwise the original sku
 */
export function getDefaultOpaqueSku(sku: string, app: Vertical) {
  return (
    productSkuNames[app].find((product) => {
      return product.humanSku === sku
    })?.sku || sku
  )
}

/**
 * Gets a plan from the defaults lists for current app by sku
 *
 * @param sku - The sku of the plan to fetch (may be opaque or human readable)
 */
export function getDefaultPlan(sku: string) {
  const opaqueSku = getOpaqueSku(sku)
  const matchSku = opaqueSku ? opaqueSku : sku
  const app = 'fish'
  const unifiedPlanList = [
    ...defaultProducts[app].result.subscriptionOfferings.tiers,
    ...onboardingDefaultProducts[app].result.subscriptionOfferings.tiers,
  ]
  return unifiedPlanList.find((plan) => {
    return plan.sku === matchSku
  })
}

/**
 * Calculates if two plans are equivalent based on membership level and term
 * @param planA
 * @param planB
 * @returns - True if plans are equivalent, false otherwise
 */
export function plansAreEquivalent(
  planA: Plan | undefined,
  planB: Plan | undefined
) {
  // if product levels and terms are the same, they're equivalent plans
  if (
    planA?.level === planB?.level &&
    planA?.term.unit === planB?.term.unit &&
    planA?.term.amount === planB?.term.amount
  ) {
    return true
  }
  return false
}

/**
 * Returns available subscription plans
 * @param [hasCoupon=false] - Needed for toggling intro offers via a feature flag, false by default
 */
export async function getFlexPlans(
  /**
   * *(AI-generated)*
   * Filter to apply to query
   */
  filter: { filter: SubscriptionOfferingFilter; offerTags: string[] } = {
    filter: { promptLocation: 'PICK_YOUR_PLAN' },
    offerTags: [],
  },
  hasCoupon = false
) {
  const client = await getApolloClientLazy()
  return client.query<SubscriptionProductsResponse>({
    query: hasCoupon ? plansQuery() : offerTagQuery(),
    variables: filter,
  })
}

const offeringsFragment = gql`
  fragment offerings_fragment on SubscriptionOfferingTier {
    sku
    name
    price
    level
    description
    trialTerm {
      unit
      amount
    }
    term {
      amount
      unit
    }
  }
`

/**
 * *(AI-generated)*
 * Returns the GraphQL query for fetching subscription plan offerings.
 */
export const plansQuery = () => {
  return gql`
    ${offeringsFragment}
    query GetSubscriptionOfferings($filter: SubscriptionOfferingFilter) {
      subscriptionOfferings(filter: $filter) {
        tiers {
          ...offerings_fragment
        }
      }
    }
  `
}

/**
 * *(AI-generated)*
 * GraphQL query for offer tags.
 */
export const offerTagQuery = () => {
  return gql`
    ${offeringsFragment}
    query GetSubscriptionOfferings(
      $offerTags: [String]
      $filter: SubscriptionOfferingFilter
    ) {
      subscriptionOfferings(filter: $filter) {
        tiers(offerTags: $offerTags) {
          ...offerings_fragment
          introOffer {
            price
            term {
              amount
              unit
            }
          }
          offerTag
        }
      }
    }
  `
}

/**
 * *(AI-generated)*
 * GraphQL query for promotional offers.
 */
export const promoOffersQuery = () => {
  return gql`
    ${offeringsFragment}
    query GetSubscriptionOfferings($filter: SubscriptionOfferingFilter) {
      subscriptionOfferings(filter: $filter) {
        tiers {
          ...offerings_fragment
          offerTag
          discount {
            amount
            unit
          }
        }
      }
    }
  `
}
